import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listInventory } from '../../lib/api';
import type { InventoryItem } from '../../types';

interface Props {
  selectedId: number | null;
  onSelect: (item: InventoryItem) => void;
}

export default function InventoryList({ selectedId, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['inventory', debouncedSearch],
    queryFn: () => listInventory({ search: debouncedSearch, limit: 200 }),
  });

  function handleSearchChange(v: string) {
    setSearch(v);
    clearTimeout((handleSearchChange as { _t?: ReturnType<typeof setTimeout> })._t);
    const t = setTimeout(() => setDebouncedSearch(v), 300);
    (handleSearchChange as { _t?: ReturnType<typeof setTimeout> })._t = t;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-gray-200 flex gap-2">
        <input
          className="input flex-1"
          placeholder="Search parts..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <button
          className="btn-primary px-3 py-2 text-xs"
          onClick={() => setDebouncedSearch(search)}
        >
          Search
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {isLoading && (
          <div className="flex items-center justify-center py-12 text-sm text-gray-500">
            Loading...
          </div>
        )}
        {!isLoading && data?.items.length === 0 && (
          <div className="flex items-center justify-center py-12 text-sm text-gray-500">
            No parts found.
          </div>
        )}
        {data?.items.map((item) => (
          <button
            key={item.id}
            onClick={() => onSelect(item)}
            className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition-colors
              ${selectedId === item.id
                ? 'bg-brand-50 border-l-2 border-brand-600'
                : 'hover:bg-gray-50'
              }`}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
              <p className="text-xs text-gray-500">{item.part_number}</p>
            </div>
            <span className={`text-sm font-bold shrink-0 ${item.qty <= 2 ? 'text-red-600' : 'text-gray-700'}`}>
              {item.qty}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
